/**
 * surface-detect.ts — Boot-time surface detection.
 *
 * Determina qual superficie UI deve ser renderizada (admin | business | landing)
 * a partir do hostname do browser e/ou da env var de override em dev.
 *
 * Implementa Decision D-C1 (03.5-CONTEXT.md):
 *   Firebase Hosting multi-site com bundle compartilhado + hostname check.
 *   `main.tsx` chama detectSurface() no boot e renderiza a route tree correspondente.
 *
 * Dev override (VITE_SURFACE_OVERRIDE):
 *   Em dev, localhost:3001 não tem subdomain real. Setar VITE_SURFACE_OVERRIDE=admin
 *   (ou 'business') em .env.local para testar uma superfície específica localmente.
 *   Se não setada, detectSurface() retorna 'landing' para localhost (comportamento padrão).
 *
 * Prod:
 *   VITE_SURFACE_OVERRIDE NÃO é setada. A detecção ocorre por hostname real:
 *     - admin.troovee.app → 'admin'
 *     - business.troovee.app → 'business'
 *     - troovee.app (ou qualquer outro) → 'landing'
 *
 * @see 03.5-CONTEXT.md § D-C1 hostname check
 * @see 03.5-RESEARCH.md § Pattern 10 — Vite Dev Mode: Surface Override
 */

/** Superfície de UI renderizada no boot via hostname check. */
export type Surface = 'admin' | 'business' | 'landing';

const VALID_SURFACES: readonly Surface[] = ['admin', 'business', 'landing'];

/**
 * Detecta a superfície ativa para o runtime atual.
 *
 * Prioridade de resolução:
 * 1. VITE_SURFACE_OVERRIDE (dev only) — se definida e válida, retorna override.
 * 2. window.location.hostname vs VITE_ADMIN_HOST / VITE_BUSINESS_HOST.
 * 3. Fallback: 'landing' (hosts desconhecidos, localhost sem override).
 *
 * @returns 'admin' | 'business' | 'landing'
 */
export function detectSurface(): Surface {
  // 1. Dev override — funciona apenas quando VITE_SURFACE_OVERRIDE está setada.
  //    Tipagem explicita: Vite embute string literal em build time; undefined em ausência.
  const override = import.meta.env.VITE_SURFACE_OVERRIDE as string | undefined;
  if (override && (VALID_SURFACES as readonly string[]).includes(override)) {
    return override as Surface;
  }

  // 2. Hostname check contra vars de ambiente de prod/staging.
  const host = window.location.hostname;
  if (host === import.meta.env.VITE_ADMIN_HOST) return 'admin';
  if (host === import.meta.env.VITE_BUSINESS_HOST) return 'business';

  // 3. Fallback: troovee.app (apex landing) OU localhost sem override.
  return 'landing';
}
